import React from 'react';
import { Breakpoints, Layout, styled } from '@volkswagen-onehub/components-core';
import { DataCollection } from '@esportal/portal-service';
import { Form0 } from '../forms/form0';
import { Form1 } from '../forms/form1';
import { Form2 } from '../forms/form2';
import { PortalContext } from '../../../context/PortalContext';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { formatOwnership } from '../../../tracking/trackingHelper';

const FormContainer = styled.div`
	height: 100%;
	width: 100%;
	align-content: center;

	@media screen and (min-width: 960px) {
		height: 80vh;
	}
`;

const FadeAnimation = styled.div<{ fade: boolean }>`
	transition: opacity 0.5s ease-in-out;
	opacity: ${({ fade }: { fade: boolean }) => (fade ? 0 : 1)};
`;

export const EndFormBackgroundContainer = styled.div`
	position: absolute;
	top: 0;
	left: 0;

	height: 100vh;
	width: 100%;
	background-color: #4cc7f4;
`;

export const RelativeContainer = styled.div`
	position: relative;
	top: 10vh;
	height: 80vh;
	z-index: 100;

	@media screen and (min-width: 560px) {
		height: 100vh;
		top: 0vh;
		align-content: center;
	}
`;

export const StyledIcon = styled.div`
	& > svg {
		width: 150px;

		@media screen and (min-width: 960px) and (max-width: 1279px) {
			width: 170px;
		}

		@media screen and (min-width: 1280px) and (max-width: 1599px) {
			width: 180px;
		}

		@media screen and (min-width: 1600px) and (max-width: 1919px) {
			width: 210px;
		}

		@media screen and (min-width: 1920px) {
			width: 240px;
		}
	}
`;

export const formLayout = {
	[Breakpoints.default]: [
		{ name: '.', columns: 1 },
		{ name: 'a', columns: 22 },
		{ name: '.', columns: 1 },
	],
	[Breakpoints.b1600]: [
		{ name: '.', columns: 2 },
		{ name: 'a', columns: 20 },
		{ name: '.', columns: 2 },
	],
	[Breakpoints.b1920]: [
		{ name: '.', columns: 3 },
		{ name: 'a', columns: 18 },
		{ name: '.', columns: 3 },
	],
	[Breakpoints.b2560]: [
		{ name: '.', columns: 5 },
		{ name: 'a', columns: 14 },
		{ name: '.', columns: 5 },
	],
};

export type DataCollectionStep = {
	name: string;
	visibleStep: boolean;
	questionLevel: number;
	ctaAnswers: { value: string; stepsToShow?: string[] }[] | null;
};

export const DataCollectionForm: React.FC<{
	dataCollection: DataCollection | null;
	handleCloseFormLayer: any;
	setIsLastStep: (value: boolean) => void;
}> = ({ dataCollection, handleCloseFormLayer, setIsLastStep }) => {
	const { portalService } = React.useContext(PortalContext);
	const formStepsRef = React.useRef<DataCollectionStep[]>([]);

	const trackingManager = useTrackingManager();

	const [dataCollectionCopy, setDataCollectionCopy] = React.useState<DataCollection | null>(dataCollection);
	const continueFormStep = React.useRef(0);
	const [steps, setSteps] = React.useState({
		previousStep: 0,
		actualStep: 0,
	});
	const [fade, setFade] = React.useState(false);
	const [visibleLevel, setVisibleLevel] = React.useState(dataCollectionCopy?.userLevel);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const trackingTriggered = React.useRef(false);

	React.useEffect(() => {
		if (userVehicles) {
			trackingManager.trackClubVWLayerLoad(
				{ contentId: 'Data Collection' },
				{ vehicle: formatOwnership(userVehicles.vehicles) },
			);
		}
	}, []);

	const renderForm = () => {
		switch (visibleLevel) {
			case 0:
				return (
					<Form0
						handleLinealNextStep={handleLinealNextStep}
						updateVisibleSteps={updateVisibleSteps}
						getInitialSteps={getInitialSteps}
						steps={steps}
					/>
				);
			case 1:
				return (
					<Form1
						handleLinealNextStep={handleLinealNextStep}
						updateVisibleSteps={updateVisibleSteps}
						getInitialSteps={getInitialSteps}
						steps={steps}
					/>
				);
			case 2:
				return (
					<Form2
						handleLinealNextStep={handleLinealNextStep}
						updateVisibleSteps={updateVisibleSteps}
						getInitialSteps={getInitialSteps}
						steps={steps}
					/>
				);
			default:
				return null;
		}
	};

	//Se Obtienen los steps del formulario seleccionado
	const getInitialSteps = (initialSteps: DataCollectionStep[]) => {
		formStepsRef.current = initialSteps;

		skipPreviousSteps(dataCollectionCopy?.lastQuestion);
	};

	const skipPreviousSteps = (targetName?: string) => {
		const targetIndex = formStepsRef.current.findIndex((step) => step.name === targetName);

		if (targetIndex === -1) {
			setSteps({
				previousStep: continueFormStep.current,
				actualStep: continueFormStep.current,
			});
		} else {
			//Actualizacion de visible steps
			formStepsRef.current.forEach((step) => {
				if (step.ctaAnswers) {
					//Recorremos cada respuesta de cada pregunta del formulario
					step.ctaAnswers.forEach((ctaAnswer) => {
						const personAnswer = dataCollection?.answers.some((answer) => {
							//Comprobamos si dicha pregunta esta o no esta respondida
							if (answer.question !== step.name) {
								return false;
							}
							//Recorresmos cada "answer0x" para ver si la respuesta del usuario es la misma que la del formulario
							const answerValues = Object.keys(answer)
								.filter((key) => key.startsWith('answer'))
								.map((key) => answer[key as keyof typeof answer]);
							return answerValues.includes(ctaAnswer.value);
						});
						//Ya sabiendo que preguntas se han respondido ademas miramos si esa respuesta tiene steps to show
						if (personAnswer && ctaAnswer.stepsToShow) {
							ctaAnswer.stepsToShow.forEach((stepNameToShow) => {
								const stepToShow = formStepsRef.current.find((step) => step.name === stepNameToShow);
								if (stepToShow) {
									stepToShow.visibleStep = true;
								}
							});
						}
					});
				}
			});

			let actualStep = targetIndex + 1;
			while (actualStep < formStepsRef.current.length && !formStepsRef.current[actualStep].visibleStep) {
				actualStep++;
			}
			setSteps({
				previousStep: steps.actualStep,
				actualStep: actualStep,
			});
		}
	};

	const updateVisibleSteps = React.useCallback((newSteps: string[], isSelected: boolean) => {
		formStepsRef.current = formStepsRef.current.map((step) =>
			newSteps.includes(step.name) ? { ...step, visibleStep: isSelected } : step,
		);
	}, []);

	const handleLinealNextStep = React.useCallback(
		(answer?: string[]) => {
			let nextStepIndex = steps.actualStep + 1;

			if (nextStepIndex == formStepsRef.current.length) {
				if (answer && answer[0] == 'COMPLETED_ALL_FORMS') {
					handleCloseFormLayer();
					return;
				}
				setFade(true);
				setTimeout(() => {
					setSteps({
						previousStep: 0,
						actualStep: 0,
					});
					setVisibleLevel(dataCollectionCopy?.userLevel);
					continueFormStep.current = 1;
					trackingTriggered.current = false;
					setFade(false);
				}, 500);
				return;
			}

			while (nextStepIndex < formStepsRef.current.length && !formStepsRef.current[nextStepIndex].visibleStep) {
				nextStepIndex++;
			}

			if (nextStepIndex + 1 === formStepsRef.current.length) {
				setIsLastStep(true);
				if (!trackingTriggered.current) {
					setTimeout(() => {
						trackingManager.trackClubVWFormSubmissionMessageLoad(
							{
								FormName: `vw-club-data_collection-${visibleLevel}`,
								FormStart: false,
								FormTypeMultiple: ['otherform'],
							},
							{ contentId: `Completed Data Collection Form-${visibleLevel}` },
							{ vehicle: formatOwnership(userVehicles?.vehicles) },
						);
					}, 500);
				}
				trackingTriggered.current = true;
			} else {
				setIsLastStep(false);
			}

			if (answer) {
				submitAnswers(answer, nextStepIndex);
			}

			if (nextStepIndex < formStepsRef.current.length) {
				setFade(true);
				setTimeout(() => {
					setSteps({
						previousStep: steps.actualStep,
						actualStep: nextStepIndex,
					});
					setFade(false);
				}, 500);
			}
		},
		[steps.actualStep, dataCollectionCopy],
	);

	//Subimos la respuesta al back y actualizamos DataCollection
	const submitAnswers = (answer: string[], nextStepIndex: number) => {
		const questionName = formStepsRef.current[steps.actualStep].name;
		const questionLevel = formStepsRef.current[steps.actualStep].questionLevel;

		const answers = answer.reduce((answersObject, answer, i) => {
			const answerKey = `answer${(i + 1).toString().padStart(2, '0')}`;
			answersObject[answerKey] = answer || '?';
			return answersObject;
		}, {} as any);

		const mappedAnswers = [
			{
				question: questionName,
				...answers,
				questionLevel: questionLevel,
				nextLevel: nextStepIndex === formStepsRef.current.length - 1 ? true : false,
			},
		] as string[];

		portalService.newPersonAnswerDataCollection(mappedAnswers).subscribe({
			next: (data: DataCollection | null) => {
				setDataCollectionCopy(data);
			},
		});
	};

	return (
		<FormContainer className="form-container">
			<Layout data-testid="form-layout" appearance={formLayout} allowOverflowingContent>
				<FadeAnimation className="fade-animation" fade={fade}>
					{renderForm()}
				</FadeAnimation>
			</Layout>
		</FormContainer>
	);
};
